import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axiosInstance from "../../services/API";

// Async thunks for API calls
export const getLiveStreamConfig = createAsyncThunk(
    "liveStream/getConfig",
    async (_, { rejectWithValue }) => {
        try {
            const response = await axiosInstance.get("/livestream/config/");
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);

export const startLiveStream = createAsyncThunk(
    "liveStream/start",
    async ({ title, description }, { rejectWithValue }) => {
        try {
            const response = await axiosInstance.post("/livestream/start/", {
                title,
                description,
            });
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);

export const stopLiveStream = createAsyncThunk(
    "liveStream/stop",
    async (streamId, { rejectWithValue }) => {
        try {
            const response = await axiosInstance.post(`/livestream/stop/${streamId}/`);
            return { streamId, ...response.data };
        } catch (error) {
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);

export const getLiveStreams = createAsyncThunk(
    "liveStream/getList",
    async (_, { rejectWithValue }) => {
        try {
            const response = await axiosInstance.get("/livestream/list/");
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);

export const getLiveStreamDetails = createAsyncThunk(
    "liveStream/getDetails",
    async (streamId, { rejectWithValue }) => {
        try {
            const response = await axiosInstance.get(`/livestream/${streamId}/`);
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);

const initialState = {
    // Configuration data from AWS
    config: {
        ingest_endpoint: null,
        stream_key: null,
        playback_url: null,
    },

    // Current active stream (for creator)
    activeStream: {
        stream_id: null,
        title: null,
        description: null,
        playback_url: null,
        is_live: false,
        started_at: null,
        ended_at: null,
        host: null,
    },

    // List of all live streams
    streams: [],

    // Currently viewing stream (for viewers)
    currentViewingStream: null,

    // Loading states
    loading: {
        config: false,
        starting: false,
        stopping: false,
        fetchingStreams: false,
        fetchingDetails: false,
    },

    // Error states
    errors: {
        config: null,
        starting: null,
        stopping: null,
        fetchingStreams: null,
        fetchingDetails: null,
    },

    // UI states
    ui: {
        isCreatingStream: false,
        showStreamModal: false,
        streamQuality: 'auto',
        isMuted: false,
        volume: 1,
    }
};

const liveStreamSlice = createSlice({
    name: "liveStream",
    initialState,
    reducers: {
        // UI Actions
        setCreatingStream: (state, action) => {
            state.ui.isCreatingStream = action.payload;
        },
        setStreamModal: (state, action) => {
            state.ui.showStreamModal = action.payload;
        },
        setStreamQuality: (state, action) => {
            state.ui.streamQuality = action.payload;
        },
        setMuted: (state, action) => {
            state.ui.isMuted = action.payload;
        },
        setVolume: (state, action) => {
            state.ui.volume = action.payload;
        },

        // Stream management
        setCurrentViewingStream: (state, action) => {
            state.currentViewingStream = action.payload;
        },
        clearActiveStream: (state) => {
            state.activeStream = initialState.activeStream;
        },
        clearErrors: (state) => {
            state.errors = initialState.errors;
        },

        // Update stream status in real-time
        updateStreamStatus: (state, action) => {
            const { streamId, is_live } = action.payload;
            if (state.activeStream.stream_id === streamId) {
                state.activeStream.is_live = is_live;
            }

            const streamIndex = state.streams.findIndex(s => s.stream_id === streamId);
            if (streamIndex !== -1) {
                state.streams[streamIndex].is_live = is_live;
            }
        },
    },
    extraReducers: (builder) => {
        // Get Live Stream Config
        builder
            .addCase(getLiveStreamConfig.pending, (state) => {
                state.loading.config = true;
                state.errors.config = null;
            })
            .addCase(getLiveStreamConfig.fulfilled, (state, action) => {
                state.loading.config = false;
                state.config = action.payload;
            })
            .addCase(getLiveStreamConfig.rejected, (state, action) => {
                state.loading.config = false;
                state.errors.config = action.payload;
            });

        // Start Live Stream
        builder
            .addCase(startLiveStream.pending, (state) => {
                state.loading.starting = true;
                state.errors.starting = null;
            })
            .addCase(startLiveStream.fulfilled, (state, action) => {
                state.loading.starting = false;

                // Update both activeStream and config with the response data
                state.activeStream = {
                    stream_id: action.payload.stream_id,
                    title: action.payload.title,
                    description: action.payload.description,
                    playback_url: action.payload.playback_url,
                    is_live: action.payload.is_live || true,
                    started_at: new Date().toISOString(),
                    ended_at: null,
                    host: action.meta?.arg?.userId || null, // Get from action meta if available
                };

                // Update config with stream-specific data for React app usage
                state.config = {
                    ...state.config,
                    ingest_endpoint: action.payload.ingest_endpoint,
                    stream_key: action.payload.stream_key,
                    playback_url: action.payload.playback_url,
                    channel_arn: action.payload.channel_arn,
                };

                state.ui.isCreatingStream = false;
            })
            .addCase(startLiveStream.rejected, (state, action) => {
                state.loading.starting = false;
                state.errors.starting = action.payload;
            });

        // Stop Live Stream
        builder
            .addCase(stopLiveStream.pending, (state) => {
                state.loading.stopping = true;
                state.errors.stopping = null;
            })
            .addCase(stopLiveStream.fulfilled, (state, action) => {
                state.loading.stopping = false;
                if (state.activeStream.stream_id === action.payload.streamId) {
                    state.activeStream.is_live = false;
                    state.activeStream.ended_at = new Date().toISOString();
                }
            })
            .addCase(stopLiveStream.rejected, (state, action) => {
                state.loading.stopping = false;
                state.errors.stopping = action.payload;
            });

        // Get Live Streams List
        builder
            .addCase(getLiveStreams.pending, (state) => {
                state.loading.fetchingStreams = true;
                state.errors.fetchingStreams = null;
            })
            .addCase(getLiveStreams.fulfilled, (state, action) => {
                state.loading.fetchingStreams = false;
                state.streams = action.payload;
            })
            .addCase(getLiveStreams.rejected, (state, action) => {
                state.loading.fetchingStreams = false;
                state.errors.fetchingStreams = action.payload;
            });

        // Get Live Stream Details
        builder
            .addCase(getLiveStreamDetails.pending, (state) => {
                state.loading.fetchingDetails = true;
                state.errors.fetchingDetails = null;
            })
            .addCase(getLiveStreamDetails.fulfilled, (state, action) => {
                state.loading.fetchingDetails = false;
                state.currentViewingStream = action.payload;
            })
            .addCase(getLiveStreamDetails.rejected, (state, action) => {
                state.loading.fetchingDetails = false;
                state.errors.fetchingDetails = action.payload;
            });
    },
});

export const {
    setCreatingStream,
    setStreamModal,
    setStreamQuality,
    setMuted,
    setVolume,
    setCurrentViewingStream,
    clearActiveStream,
    clearErrors,
    updateStreamStatus,
} = liveStreamSlice.actions;

export default liveStreamSlice.reducer;